import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import ProgressBar from '../components/ui/ProgressBar';
import { Survey, SurveyAnswer } from '../types';
import { surveyApi } from '../api/survey';
import toast from 'react-hot-toast';

const SurveyResponsePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Record<string, string | number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const demoSurvey: Survey = {
      id: id || '1', company_id: '1', title: 'Enquête QVT 2026',
      description: 'Aidez-nous à améliorer votre qualité de vie au travail.',
      questions: [
        { id: 'q1', text: 'Comment évaluez-vous votre satisfaction globale au travail ?', type: 'rating', required: true, order: 0 },
        { id: 'q2', text: "Comment jugez-vous l'équilibre entre votre vie professionnelle et personnelle ?", type: 'rating', required: true, order: 1 },
        { id: 'q3', text: 'Vous sentez-vous soutenu(e) par votre manager ?', type: 'yes_no', required: true, order: 2 },
        { id: 'q4', text: 'Quels aspects aimeriez-vous améliorer ?', type: 'text', required: false, order: 3 },
      ],
      status: 'active', access_mode: 'link', response_count: 0, created_by: '', created_at: '',
    };
    surveyApi.getPublic(id!).then(res => {
      const d = res.data as any;
      setSurvey(d.survey || d);
    }).catch(() => setSurvey(demoSurvey))
      .finally(() => setLoading(false));
  }, [id]);

  const setAnswer = (questionId: string, value: string | number) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (!survey) return;
    const required = survey.questions.filter(q => q.required);
    const missing = required.filter(q => answers[q.id] === undefined || answers[q.id] === '');
    if (missing.length > 0) {
      toast.error('Veuillez répondre à toutes les questions obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      const answerList: SurveyAnswer[] = Object.entries(answers).map(([qid, val]) => ({
        question_id: qid,
        value: val,
      }));
      await surveyApi.respond(survey.id, { answers: answerList });
    } catch {
      // Demo mode
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  const progress = survey ? Math.round((Object.keys(answers).length / survey.questions.length) * 100) : 0;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg)' }}>
        <div className="skeleton" style={{ width: 400, height: 300, borderRadius: 'var(--radius-xl)' }} />
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg)', padding: '2rem' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-2xl)',
            padding: '3rem',
            textAlign: 'center',
            maxWidth: 480,
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'var(--color-success-light)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={{ marginBottom: '0.5rem' }}>Merci pour votre participation !</h2>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Votre réponse a été enregistrée de manière anonyme. Elle contribuera à améliorer les conditions de travail.
          </p>
        </motion.div>
      </div>
    );
  }

  if (!survey) return null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', padding: '2rem' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div
            style={{
              background: 'var(--color-primary)',
              borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
              padding: '2rem',
              color: '#fff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                J
              </div>
              <span style={{ fontWeight: 600 }}>JULIA</span>
            </div>
            <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginBottom: '0.5rem', color: '#fff' }}>
              {survey.title}
            </h1>
            {survey.description && (
              <p style={{ opacity: 0.85, fontSize: 'var(--font-size-base)' }}>{survey.description}</p>
            )}
          </div>
        </motion.div>

        {/* Progress */}
        <div style={{ padding: '1rem 0' }}>
          <ProgressBar value={progress} color="var(--color-accent)" height={6} showLabel />
        </div>

        {/* Questions */}
        <div
          style={{
            background: 'var(--color-surface)',
            borderRadius: '0 0 var(--radius-xl) var(--radius-xl)',
            padding: '2rem',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          {survey.questions.map((q, i) => (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              style={{
                marginBottom: '2rem',
                paddingBottom: '1.5rem',
                borderBottom: i < survey.questions.length - 1 ? '1px solid var(--color-border-light)' : 'none',
              }}
            >
              <p style={{ fontWeight: 500, marginBottom: '0.75rem' }}>
                {q.text}
                {q.required && <span style={{ color: 'var(--color-danger)', marginLeft: 4 }}>*</span>}
              </p>

              {q.type === 'rating' && (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[1, 2, 3, 4, 5].map((val) => (
                    <button
                      key={val}
                      onClick={() => setAnswer(q.id, val)}
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 'var(--radius-lg)',
                        border: '2px solid',
                        borderColor: answers[q.id] === val ? 'var(--color-primary)' : 'var(--color-border)',
                        background: answers[q.id] === val ? 'var(--color-primary)' : 'var(--color-surface)',
                        color: answers[q.id] === val ? '#fff' : 'var(--color-text)',
                        fontWeight: 600,
                        fontSize: 'var(--font-size-lg)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-fast)',
                      }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              )}

              {q.type === 'yes_no' && (
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {[{ val: 'oui', label: 'Oui' }, { val: 'non', label: 'Non' }].map(({ val, label }) => (
                    <button
                      key={val}
                      onClick={() => setAnswer(q.id, val)}
                      style={{
                        padding: '0.625rem 2rem',
                        borderRadius: 'var(--radius-md)',
                        border: '2px solid',
                        borderColor: answers[q.id] === val ? 'var(--color-primary)' : 'var(--color-border)',
                        background: answers[q.id] === val ? 'var(--color-primary)' : 'var(--color-surface)',
                        color: answers[q.id] === val ? '#fff' : 'var(--color-text)',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all var(--transition-fast)',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {q.type === 'text' && (
                <textarea
                  value={(answers[q.id] as string) || ''}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  placeholder="Votre réponse..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: 'var(--font-size-base)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'var(--font-family)',
                  }}
                />
              )}

              {q.type === 'multiple_choice' && q.options && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setAnswer(q.id, opt)}
                      style={{
                        padding: '0.625rem 1rem',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid',
                        borderColor: answers[q.id] === opt ? 'var(--color-primary)' : 'var(--color-border)',
                        background: answers[q.id] === opt ? `var(--color-primary)11` : 'var(--color-surface)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontWeight: answers[q.id] === opt ? 500 : 400,
                        transition: 'all var(--transition-fast)',
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          ))}

          <Button fullWidth size="lg" onClick={handleSubmit} loading={submitting}>
            Envoyer mes réponses
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SurveyResponsePage;
